/* eslint-disable */

import React, { useState, useEffect } from "react";
import { useAuthStore } from "@/features/auth/store/useAuthStore";
import { refreshTokenApi } from "@/features/auth/api/auth.api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

interface AuthDebugPanelProps {
  enabled?: boolean;
}

export const AuthDebugPanel: React.FC<AuthDebugPanelProps> = ({
  enabled = false,
}) => {
  // Only render if explicitly enabled
  if (!enabled) return null;

  const {
    token,
    refresh_token,
    expiresAt,
    user,
    login,
    logout,
    debugExpireToken,
  } = useAuthStore();

  const [currentTime, setCurrentTime] = useState(Date.now());
  const [refreshLogs, setRefreshLogs] = useState<string[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Update current time every second
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Capture console logs for refresh operations
  useEffect(() => {
    const originalLog = console.log;
    console.log = (...args) => {
      const message = args.join(" ");
      if (
        message.includes("[AUTO-REFRESH") ||
        message.includes("[AXIOS-INTERCEPTOR")
      ) {
        setRefreshLogs((prev) => [...prev.slice(-9), message]); // Keep last 10 logs
      }
      originalLog.apply(console, args);
    };

    return () => {
      console.log = originalLog;
    };
  }, []);

  const handleManualRefresh = async () => {
    if (!refresh_token || isRefreshing) return;

    setIsRefreshing(true);
    try {
      const startTime = Date.now();
      console.log("🧪 [DEBUG-PANEL] Manual refresh triggered");
      const response = await refreshTokenApi(refresh_token);
      if (user) {
        login({
          user,
          token: response.access_token || response.token,
          refresh_token: response.refresh_token || refresh_token,
        });
      }
      const endTime = Date.now();
      const duration = endTime - startTime;
      console.log(
        `🧪 [DEBUG-PANEL] Manual refresh completed (took ${duration}ms)`,
      );
    } catch (error) {
      console.error("🧪 [DEBUG-PANEL] Manual refresh failed:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleDirectRefresh = async () => {
    if (!refresh_token) return;

    try {
      const startTime = Date.now();
      console.log("🧪 [DEBUG-PANEL] Direct API refresh triggered");
      const response = await refreshTokenApi(refresh_token);
      const endTime = Date.now();
      const duration = endTime - startTime;

      if (user) {
        login({
          user,
          token: response.access_token || response.token,
          refresh_token: response.refresh_token || refresh_token,
        });
      }
      console.log(
        `🧪 [DEBUG-PANEL] Direct refresh successful (took ${duration}ms)`,
      );
    } catch (error) {
      console.error("🧪 [DEBUG-PANEL] Direct refresh failed:", error);
    }
  };

  const handleMockExpire = () => {
    console.log("🧪 [DEBUG-PANEL] Mocking token expiration");
    debugExpireToken();
  };

  const handleMockExpireSoon = () => {
    const soonExpiry = Date.now() + 30000; // 30 seconds from now
    console.log("🧪 [DEBUG-PANEL] Mocking token expiring soon");
    useAuthStore.setState({ expiresAt: soonExpiry });
  };

  const clearLogs = () => {
    setRefreshLogs([]);
  };

  if (!isVisible) {
    return (
      <Button
        onClick={() => setIsVisible(true)}
        className="fixed right-4 bottom-4 z-50 bg-purple-600 hover:bg-purple-700"
        size="sm"
      >
        🔧 Debug Auth
      </Button>
    );
  }

  const timeUntilExpiry = expiresAt ? expiresAt - currentTime : null;
  const isExpired = timeUntilExpiry !== null && timeUntilExpiry <= 0;
  const isExpiringSoon =
    timeUntilExpiry !== null && timeUntilExpiry <= 2 * 60 * 1000; // 2 minutes

  return (
    <Card className="fixed right-4 bottom-4 z-50 max-h-[80vh] w-96 overflow-auto border-2 bg-white shadow-2xl">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">🔧 Auth Debug Panel</CardTitle>
          <Button
            onClick={() => setIsVisible(false)}
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
          >
            ✕
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Token Status */}
        <div>
          <h4 className="mb-2 font-semibold">Token Status</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span>Has Token:</span>
              <Badge variant={token ? "default" : "destructive"}>
                {token ? "✓" : "✗"}
              </Badge>
            </div>
            <div className="flex justify-between">
              <span>Has Refresh:</span>
              <Badge variant={refresh_token ? "default" : "destructive"}>
                {refresh_token ? "✓" : "✗"}
              </Badge>
            </div>
            <div className="flex justify-between">
              <span>Has User:</span>
              <Badge variant={user ? "default" : "destructive"}>
                {user ? "✓" : "✗"}
              </Badge>
            </div>
            <div className="flex justify-between">
              <span>Is Refreshing:</span>
              <Badge variant={isRefreshing ? "secondary" : "outline"}>
                {isRefreshing ? "🔄" : "⏸️"}
              </Badge>
            </div>
          </div>
        </div>

        <Separator />

        {/* Token Timing */}
        <div>
          <h4 className="mb-2 font-semibold">Token Timing</h4>
          <div className="space-y-2 text-sm">
            <div>
              <span className="font-medium">Current Time:</span>
              <div className="text-xs text-gray-600">
                {new Date(currentTime).toISOString()}
              </div>
            </div>
            {expiresAt && (
              <>
                <div>
                  <span className="font-medium">Expires At:</span>
                  <div className="text-xs text-gray-600">
                    {new Date(expiresAt).toISOString()}
                  </div>
                </div>
                <div className="flex justify-between">
                  <span>Time Until Expiry:</span>
                  <Badge
                    variant={
                      isExpired
                        ? "destructive"
                        : isExpiringSoon
                          ? "secondary"
                          : "default"
                    }
                  >
                    {timeUntilExpiry !== null
                      ? `${Math.round(timeUntilExpiry / 1000)}s`
                      : "N/A"}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span>Status:</span>
                  <Badge
                    variant={
                      isExpired
                        ? "destructive"
                        : isExpiringSoon
                          ? "secondary"
                          : "default"
                    }
                  >
                    {isExpired
                      ? "EXPIRED"
                      : isExpiringSoon
                        ? "EXPIRING SOON"
                        : "VALID"}
                  </Badge>
                </div>
              </>
            )}
          </div>
        </div>

        <Separator />

        {/* Debug Actions */}
        <div>
          <h4 className="mb-2 font-semibold">Debug Actions</h4>
          <div className="space-y-2">
            <Button
              onClick={handleManualRefresh}
              disabled={isRefreshing || !refresh_token}
              className="w-full"
              size="sm"
            >
              {isRefreshing ? "🔄 Refreshing..." : "🔄 Manual Refresh (Hook)"}
            </Button>

            <Button
              onClick={handleDirectRefresh}
              disabled={!refresh_token}
              variant="outline"
              className="w-full"
              size="sm"
            >
              🚀 Direct API Refresh
            </Button>

            <Button
              onClick={handleMockExpire}
              variant="destructive"
              className="w-full"
              size="sm"
            >
              ⏰ Mock Token Expired
            </Button>

            <Button
              onClick={handleMockExpireSoon}
              variant="secondary"
              className="w-full"
              size="sm"
            >
              ⚠️ Mock Expiring Soon (30s)
            </Button>

            <Button
              onClick={logout}
              variant="outline"
              className="w-full"
              size="sm"
            >
              🚪 Force Logout
            </Button>
          </div>
        </div>

        <Separator />

        {/* Refresh Logs */}
        <div>
          <div className="mb-2 flex items-center justify-between">
            <h4 className="font-semibold">Recent Logs</h4>
            <Button
              onClick={clearLogs}
              variant="ghost"
              size="sm"
              className="h-6 px-2"
            >
              Clear
            </Button>
          </div>
          <div className="max-h-32 overflow-y-auto rounded bg-gray-50 p-2 text-xs">
            {refreshLogs.length === 0 ? (
              <div className="text-gray-500">No logs yet...</div>
            ) : (
              refreshLogs.map((log, index) => (
                <div key={index} className="mb-1 font-mono">
                  {log}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Token Preview */}
        {token && (
          <>
            <Separator />
            <div>
              <h4 className="mb-2 font-semibold">Token Preview</h4>
              <div className="rounded bg-gray-50 p-2 text-xs break-all">
                {token.substring(0, 50)}...
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
